<template>
  <div
    :class="['notes-container', isDarkMode ? 'dark' : 'light']"
    style="height: 100%">
    <div id="editor"></div>
  </div>
</template>

<script lang="ts" setup>
  import { onMounted, onBeforeUnmount, ref } from 'vue'
  import { Crepe } from '@milkdown/crepe'
  import '@milkdown/crepe/theme/common/style.css'
  import '@milkdown/crepe/theme/frame.css'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { SoftwareOut } from '@/client'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { useI18n } from 'vue-i18n'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'

  type Props = {
    application: SoftwareOut
  }

  const props = defineProps<Props>()

  const i18n = useI18n()

  const preferenceStore = usePreferenceStore()
  const applicationDetailsStore = useApplicationDetailsStore()
  const isDarkMode = computed(
    () => preferenceStore.preferences?.appearance === 'dark_theme'
  )

  const notes = ref<string | null>(null)

  type MenuApi = {
    show: (pos: number) => void
    hide: () => void
  }

  // Crepe instance
  let crepe: Crepe | null = null
  let menuApi: MenuApi | null = null
  let lastSaved = notes.value
  let animationFrameId: number | null = null

  const MENU_API_CTX_KEY = 'menuAPICtx' as const

  function attachMenuApi() {
    if (!crepe) return

    crepe.editor.action((ctx) => {
      try {
        menuApi = ctx.get(MENU_API_CTX_KEY as never) as MenuApi
      } catch (error) {
        menuApi = null
      }
    })
  }

  function handlePointerDownOutside(event: PointerEvent) {
    if (!menuApi) return

    const target = event.target as HTMLElement | null
    if (!target) return

    if (target.closest('.milkdown-slash-menu')) return
    if (target.closest('.milkdown-block-handle')) return

    const isMenuVisible = document.querySelector(
      '.milkdown-slash-menu[data-show="true"]'
    )

    if (!isMenuVisible) return

    menuApi.hide()
  }

  onMounted(() => {
    notes.value = props.application.notes || ''
    crepe = new Crepe({
      root: '#editor',
      defaultValue: notes.value || '',
      features: {
        // deactivate image and math features
        [Crepe.Feature.ImageBlock]: false,
        [Crepe.Feature.Latex]: false,
        [Crepe.Feature.Placeholder]: true,
      },
      featureConfigs: {
        [Crepe.Feature.Placeholder]: {
          text: i18n.t('notesPlaceholder'),
        },
        [Crepe.Feature.BlockEdit]: {
          blockHandle: {
            getOffset: () => ({
              mainAxis: 12,
            }),
          },
        },
      },
    })

    crepe.create()

    attachMenuApi()
    requestAnimationFrame(attachMenuApi)
    document.addEventListener('pointerdown', handlePointerDownOutside, true)

    lastSaved = notes.value || ''
    animationFrameId = requestAnimationFrame(autoSave)
  })

  onBeforeUnmount(() => {
    if (crepe) {
      crepe.destroy()
      crepe = null
    }
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
    document.removeEventListener('pointerdown', handlePointerDownOutside, true)
    menuApi = null
  })

  // Save handler
  async function saveNotes(newNotes: string) {
    if (!crepe) return
    notes.value = newNotes
    const currentApplication = applicationDetailsStore.application
    if (currentApplication) {
      currentApplication.notes = notes.value ?? ''
    }
    return await ApplicationStore.updateApplication(props.application._id, {
      notes: notes.value,
    })
  }

  function autoSave() {
    if (!crepe) return
    const current = crepe.getMarkdown()
    if (current !== lastSaved) {
      lastSaved = current
      saveNotes(current)
    }

    animationFrameId = requestAnimationFrame(autoSave)
  }
</script>

<style lang="scss" scoped>
  .notes-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  #editor {
    flex: 1 1 auto;
    height: auto;
    overflow: visible;
    position: relative;
    min-height: 0;
  }

  :deep(.milkdown) {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: var(--border-radius-xl);
    background: transparent;
    min-height: 0;
    --crepe-font-title: var(
      --font-family,
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      sans-serif
    );
  }

  :deep(.milkdown .ProseMirror) {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 20px 48px;
    border-radius: var(--border-radius-xl);
    height: 100%;
    min-height: 0;
  }

  :deep(
      .milkdown .ProseMirror h1,
      .milkdown .ProseMirror h2,
      .milkdown .ProseMirror h3,
      .milkdown .ProseMirror h4,
      .milkdown .ProseMirror h5,
      .milkdown .ProseMirror h6
    ) {
    font-family: inherit !important;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  :deep(.milkdown-slash-menu) {
    min-width: 240px;
    padding: 6px;
    border: 1px solid rgba(167, 167, 167, 0.56);
    border-radius: var(--border-radius-base);
    background: var(--sm-elevation);
    color: var(--sm-contrast);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  }

  :deep(.milkdown-slash-menu .tab-group) {
    display: none;
  }

  :deep(.milkdown-slash-menu .menu-groups) {
    padding: 4px;
    max-height: 320px;
  }

  :deep(.milkdown-slash-menu .menu-group h6) {
    display: none;
  }

  :deep(.milkdown-slash-menu .menu-group + .menu-group::before) {
    display: none;
  }

  :deep(.milkdown-slash-menu .menu-group li) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: var(--font-size-small);
    font-weight: 500;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
  }

  :deep(.milkdown-slash-menu .menu-group li svg) {
    width: 18px;
    height: 18px;
    color: var(--sm-contrast-muted);
    fill: var(--sm-contrast-muted);
  }

  :deep(.milkdown-slash-menu .menu-group li.hover),
  :deep(.milkdown-slash-menu .menu-group li.active) {
    background-color: theme('colors.gray[50]');
    color: var(--sm-contrast);
  }

  :deep(.milkdown-slash-menu .menu-group li.hover svg),
  :deep(.milkdown-slash-menu .menu-group li.active svg) {
    color: var(--sm-contrast);
    fill: var(--sm-contrast);
  }

  :deep(.milkdown-slash-menu[data-show='false']) {
    display: none;
  }

  :deep(.milkdown .milkdown-block-handle) {
    z-index: 30;
    left: -15px !important;
  }

  @media (max-width: 1024px) {
    :deep(.milkdown .ProseMirror) {
      padding: 20px 24px;
    }
  }
</style>

<style lang="scss">
  .notes-container.dark .milkdown {
    --crepe-color-background: var(--sm-dark);
    --crepe-color-surface: #2a2a2a;
    --crepe-color-surface-low: #333333;
    --crepe-color-on-background: #f5f5f5;
    --crepe-color-on-surface: #e0e0e0;
    --crepe-color-on-surface-variant: #aaaaaa;
    --crepe-color-primary: var(--sm-primary);
    --crepe-color-secondary: #333333;
    --crepe-color-outline: var(--sm-contrast-muted);
    --crepe-color-inline-code: #aaaaaa;
    --crepe-color-error: var(--sm-red);
    --crepe-color-hover: #383838;
    --crepe-color-selected: #444444;
    --crepe-color-inline-area: #3a3a3a;
  }

  .notes-container.dark .milkdown-slash-menu {
    background: var(--sm-elevation);
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.36);
  }

  .notes-container.dark .milkdown-slash-menu .menu-group li.hover,
  .notes-container.dark .milkdown-slash-menu .menu-group li.active {
    background-color: rgba(255, 255, 255, 0.08);
  }

  .ProseMirror {
    padding: 20px 48px !important;
  }
</style>
